import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiArrowLeft, FiSearch } from "react-icons/fi";
import {
    searchStudents,
    getAllocationsByStudent,
    getApplicableHostelFeeStructure,
    getHostelFeeReceiptsByStudent,
    createHostelFeeReceipt,
} from "../../services/hostelService";
import studentService from "../../services/studentService";
import { academicYearService } from "../../services/yearService";

const ROOM_TYPE_RENTS = {
    Single: 4000,
    Double: 3000,
    Triple: 1500,
    Dormitory: 1500,
};

export default function HostelFeeCollect() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const preselectedStudentId = searchParams.get("student") || "";

    const [academicYears, setAcademicYears] = useState([]);
    const [academicYear, setAcademicYear] = useState("");

    const [studentQuery, setStudentQuery] = useState("");
    const [studentResults, setStudentResults] = useState([]);
    const [student, setStudent] = useState(null);
    const [searching, setSearching] = useState(false);

    const [allocation, setAllocation] = useState(null);
    const [structure, setStructure] = useState(null);
    const [totalDue, setTotalDue] = useState(0);
    const [totalPaid, setTotalPaid] = useState(0);
    const [loadingContext, setLoadingContext] = useState(false);

    const [installmentNo, setInstallmentNo] = useState(1);
    const [installmentLabel, setInstallmentLabel] = useState("Installment 1");
    const [amountPaid, setAmountPaid] = useState("");
    const [paymentDate, setPaymentDate] = useState(() => {
        const d = new Date();
        return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    });
    const [paymentMode, setPaymentMode] = useState("Cash");
    const [chequeNo, setChequeNo] = useState("");
    const [bankName, setBankName] = useState("");
    const [transactionRef, setTransactionRef] = useState("");
    const [remarks, setRemarks] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        academicYearService.getAll()
            .then((res) => {
                const years = res.data || [];
                setAcademicYears(years);
                const active = years.find((y) => y.isActive) || years[0];
                if (active) setAcademicYear(active._id);
            })
            .catch(() => toast.error("Failed to load academic years"));
    }, []);

    useEffect(() => {
        if (!preselectedStudentId || student) return;
        studentService.getById(preselectedStudentId)
            .then((res) => setStudent(res.data))
            .catch(() => toast.error("Failed to load selected student"));
    }, [preselectedStudentId, student]);

    // Debounced student search
    useEffect(() => {
        if (!studentQuery.trim() || student) {
            setStudentResults([]);
            return;
        }
        setSearching(true);
        const timer = setTimeout(() => {
            searchStudents(studentQuery)
                .then((res) => setStudentResults(res.data || []))
                .catch(() => toast.error("Student search failed"))
                .finally(() => setSearching(false));
        }, 350);
        return () => clearTimeout(timer);
    }, [studentQuery, student]);

    // Load allocation + structure + payment history whenever student/year changes
    useEffect(() => {
        if (!student || !academicYear) return;
        setLoadingContext(true);
        setAllocation(null);
        setStructure(null);

        (async () => {
            try {
                const allocRes = await getAllocationsByStudent(student._id);
                const active = (allocRes.data || []).find((a) => a.status === "Active");
                if (!active) {
                    toast.error("This student has no active hostel allocation");
                    return;
                }
                setAllocation(active);

                const roomType = active.room?.roomType || "Double";
                const standardRent = (active.room?.roomRent !== undefined && active.room?.roomRent !== null && Number(active.room.roomRent) > 0)
                    ? Number(active.room.roomRent)
                    : (active.room?.rentPerMonth || active.room?.roomCharge || ROOM_TYPE_RENTS[roomType] || 3000);

                let struct = null;
                let total = standardRent;

                try {
                    const structRes = await getApplicableHostelFeeStructure({
                        academicYear,
                        hostel: active.hostel._id,
                        roomType,
                    });
                    struct = structRes.data?.structure;
                    total = structRes.data?.total || structRes.data?.monthlyRent || standardRent;
                } catch {
                    struct = {
                        roomType,
                        heads: [{ head: `${roomType} Room Rent (Monthly)`, amount: standardRent }],
                    };
                    total = standardRent;
                }

                setStructure(struct);
                setTotalDue(total);

                const receiptsRes = await getHostelFeeReceiptsByStudent(student._id, {
                    academicYear,
                });
                const receipts = receiptsRes.data || [];
                const paid = receipts.reduce((sum, r) => sum + r.amountPaid, 0);
                setTotalPaid(paid);
                setInstallmentNo(receipts.length + 1);
                setInstallmentLabel(`${receipts.length + 1} Month Rent (${roomType})`);
                setAmountPaid(String(total));
            } catch (err) {
                toast.error(err.response?.data?.message || "Failed to load fee context");
            } finally {
                setLoadingContext(false);
            }
        })();
    }, [student, academicYear]);

    const pending = totalDue - totalPaid;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!student) return toast.error("Select a student");
        if (!allocation) return toast.error("Student has no active hostel allocation");
        if (!amountPaid || Number(amountPaid) <= 0) return toast.error("Enter a valid amount");

        setSaving(true);
        try {
            await createHostelFeeReceipt({
                student: student._id,
                hostel: allocation.hostel._id,
                hostelAllocation: allocation._id,
                academicYear,
                hostelFeeStructure: structure?._id,
                installmentNo,
                installmentLabel,
                amountPaid: Number(amountPaid),
                paymentDate,
                paymentMode,
                chequeNo,
                bankName,
                transactionRef,
                remarks,
            });
            toast.success("Hostel fee receipt created successfully");
            navigate("/hostel-fee-receipts");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to create receipt");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Collect Hostel Fee</h1>
                    <p className="page-subtitle">Record a hostel fee installment payment for a student.</p>
                </div>
                <button className="btn btn-outline" onClick={() => navigate("/hostel-allocations")}>
                    <FiArrowLeft /> Back
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card">
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">
                                    Academic Year<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <select
                                    className="form-control"
                                    value={academicYear}
                                    onChange={(e) => setAcademicYear(e.target.value)}
                                >
                                    {academicYears.map((y) => (
                                        <option key={y._id} value={y._id}>
                                            {y.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginTop: "1rem", maxWidth: 420 }}>
                            <label className="form-label">
                                Student<span style={{ color: "var(--danger)" }}> *</span>
                            </label>
                            <div className="student-select-box">
                                {!student ? (
                                    <>
                                        <div className="student-select-search">
                                            <FiSearch />
                                            <input
                                                placeholder="Search student by name or roll number..."
                                                value={studentQuery}
                                                onChange={(e) => setStudentQuery(e.target.value)}
                                            />
                                        </div>
                                        {studentQuery && (
                                            <div className="student-select-list">
                                                {searching && (
                                                    <div className="student-select-item">Searching...</div>
                                                )}
                                                {!searching && studentResults.length === 0 && (
                                                    <div className="student-select-item">No students found</div>
                                                )}
                                                {!searching &&
                                                    studentResults.map((s) => (
                                                        <button
                                                            type="button"
                                                            key={s._id}
                                                            className="student-select-item"
                                                            onClick={() => {
                                                                setStudent(s);
                                                                setStudentResults([]);
                                                            }}
                                                        >
                                                            <div className="student-name">{s.fullName}</div>
                                                            <div className="student-meta">
                                                                {s.rollNo ? `Roll ${s.rollNo}` : ""}
                                                            </div>
                                                        </button>
                                                    ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="student-select-list">
                                        <button
                                            type="button"
                                            className="student-select-item selected"
                                            title="Click to change"
                                            onClick={() => {
                                                setStudent(null);
                                                setStudentQuery("");
                                                setAllocation(null);
                                                setStructure(null);
                                            }}
                                        >
                                            <div className="student-name">{student.fullName}</div>
                                            <div className="student-meta">
                                                {student.rollNo ? `Roll ${student.rollNo} · ` : ""}Click to change
                                            </div>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {loadingContext && (
                            <p className="form-hint" style={{ marginTop: "0.75rem" }}>
                                Loading fee details...
                            </p>
                        )}

                        {allocation && structure && (
                            <div className="stat-strip" style={{ marginTop: "1rem" }}>
                                <div className="stat-chip">
                                    <span className="stat-label">Hostel / Room</span>
                                    <span className="stat-value" style={{ fontSize: "1rem" }}>
                                        {allocation.hostel.name} · {allocation.room.roomNumber}
                                    </span>
                                </div>
                                <div className="stat-chip">
                                    <span className="stat-label">Total Due</span>
                                    <span className="stat-value">₹{totalDue.toLocaleString("en-IN")}</span>
                                </div>
                                <div className="stat-chip">
                                    <span className="stat-label">Paid So Far</span>
                                    <span className="stat-value">₹{totalPaid.toLocaleString("en-IN")}</span>
                                </div>
                                <div className="stat-chip">
                                    <span className="stat-label">Pending</span>
                                    <span
                                        className="stat-value"
                                        style={{ color: pending > 0 ? "var(--danger)" : "var(--success)" }}
                                    >
                                        ₹{pending.toLocaleString("en-IN")}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="form-grid" style={{ marginTop: "1.25rem" }}>
                            <div className="form-group">
                                <label className="form-label">
                                    Installment No<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    className="form-control"
                                    type="number"
                                    min={1}
                                    value={installmentNo}
                                    onChange={(e) => setInstallmentNo(Number(e.target.value))}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    Installment Label<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    className="form-control"
                                    value={installmentLabel}
                                    onChange={(e) => setInstallmentLabel(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    Amount Paid<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    className="form-control"
                                    type="number"
                                    min={1}
                                    value={amountPaid}
                                    onChange={(e) => setAmountPaid(e.target.value)}
                                    placeholder={pending > 0 ? `Suggested: ${pending}` : ""}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    Payment Date<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    className="form-control"
                                    value={paymentDate}
                                    onChange={(e) => setPaymentDate(e.target.value)}
                                    placeholder="DD/MM/YYYY"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    Payment Mode<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <select
                                    className="form-control"
                                    value={paymentMode}
                                    onChange={(e) => setPaymentMode(e.target.value)}
                                >
                                    <option value="Cash">Cash</option>
                                    <option value="Cheque">Cheque</option>
                                    <option value="NEFT">NEFT</option>
                                    <option value="UPI">UPI</option>
                                    <option value="DD">DD</option>
                                </select>
                            </div>

                            {paymentMode === "Cheque" && (
                                <>
                                    <div className="form-group">
                                        <label className="form-label">Cheque No</label>
                                        <input
                                            className="form-control"
                                            value={chequeNo}
                                            onChange={(e) => setChequeNo(e.target.value)}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Bank Name</label>
                                        <input
                                            className="form-control"
                                            value={bankName}
                                            onChange={(e) => setBankName(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}

                            {["NEFT", "UPI", "DD"].includes(paymentMode) && (
                                <div className="form-group">
                                    <label className="form-label">Transaction Reference</label>
                                    <input
                                        className="form-control"
                                        value={transactionRef}
                                        onChange={(e) => setTransactionRef(e.target.value)}
                                    />
                                </div>
                            )}

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Remarks</label>
                                <input
                                    className="form-control"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    placeholder="Optional"
                                />
                            </div>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.25rem" }}>
                            <button type="submit" className="btn btn-primary" disabled={saving || !allocation}>
                                <FiSave /> {saving ? "Saving..." : "Record Payment"}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}